import {
  call,
  put,
  select,
  takeEvery,
} from 'redux-saga/effects';
import {
  calculateProductsQuantity,
  fetchColors,
  fetchTypes,
  fetchProductsBySection,
  setAccessoryList,
  setColors,
  setTypes,
  setNecessaryProductsQuantity,
  setPanelsList,
  setPostsList,
  fetchHeights,
  setHeights,
  fetchFilters,
  setCornerPostsList,
  setTreeStructureSettings,
  setCornerPostAccessoryList,
  setCornerAccessoryList,
} from './actions';
import {
  ATTRIBUTES_URL,
  CONFIGURE_URL,
  PRODUCTS_URL,
} from '../../config';
import { getAccessories, getTreeStructureSettings } from './selectors';
import { setOpenModal } from '../modals/actions';
import { setValidation } from '../validation/actions';
import { setLoader } from '../loader/actions';
import getUrlFiltered from '../../utils/helper_functions/getUrlFiltered';
import responseHandler from '../../utils/helper_functions/responseHandler';

function* fetchPanels(action) {
  yield put(setLoader('panels', true));
  const response = yield call(fetch, getUrlFiltered(
    `${PRODUCTS_URL}/configurator`,
    {
      section: action.payload.fetchSettings.section,
      'attributes[main_color]': action.payload.fetchSettings.color,
      'attributes[product_type]': action.payload.fetchSettings.type,
      'attributes[height]': action.payload.fetchSettings.height,
    },
  ));
  yield call(responseHandler, {
    response,
    storeSaver: setPanelsList,
  });

  yield put(setLoader('panels', false));
}

function* fetchPosts(action) {
  yield put(setLoader('posts', true));
  const response = yield call(fetch, getUrlFiltered(
    `${PRODUCTS_URL}/configurator`,
    {
      'productGroups[]': action.payload.fetchSettings.panelGroup,
      'attributes[main_color]': action.payload.fetchSettings.color,
      section: action.payload.fetchSettings.section,
    },
  ));

  yield call(responseHandler, {
    response,
    storeSaver: setPostsList,
  });

  yield put(setLoader('posts', false));
}

function* fetchCornerPosts(action) {
  const response = yield call(fetch, getUrlFiltered(
    `${PRODUCTS_URL}/configurator`,
    {
      'productGroups[]': action.payload.fetchSettings.postGroup,
      'attributes[main_color]': action.payload.fetchSettings.color,
      section: action.payload.fetchSettings.section,
    },
  ));

  yield call(responseHandler, {
    response,
    storeSaver: setCornerPostsList,
  });
}

function* fetchAccessories(action) {
  yield put(setLoader('accessories', true));

  if (!action.payload.fetchSettings.isCorner
    && action.payload.fetchSettings.section !== 'corner_accessory') {
    const response = yield call(fetch, getUrlFiltered(
      `${PRODUCTS_URL}/configurator`,
      {
        'attributes[main_color][]': [action.payload.fetchSettings.color, ''],
        'productGroups[]': [action.payload.fetchSettings.panelGroup, action.payload.fetchSettings.postGroup],
        section: action.payload.fetchSettings.section,
      },
    ));

    yield call(responseHandler, {
      response,
      storeSaver: setAccessoryList,
    });
  } else if (action.payload.fetchSettings.section === 'corner_accessory') {
    const response = yield call(fetch, getUrlFiltered(
      `${PRODUCTS_URL}/configurator`,
      {
        'attributes[main_color][]': [action.payload.fetchSettings.color, ''],
        'productGroups[]': [action.payload.fetchSettings.panelGroup, action.payload.fetchSettings.postGroup],
        section: action.payload.fetchSettings.section,
      },
    ));

    yield call(responseHandler, {
      response,
      storeSaver: setCornerAccessoryList,
    });
  } else {
    const response = yield call(fetch, getUrlFiltered(
      `${PRODUCTS_URL}/configurator`,
      {
        'attributes[main_color][]': [action.payload.fetchSettings.color, ''],
        'productGroups[]': [action.payload.fetchSettings.cornerPostGroup],
        section: action.payload.fetchSettings.section,
      },
    ));

    yield call(responseHandler, {
      response,
      storeSaver: setCornerPostAccessoryList,
    });
  }
  yield put(setLoader('accessories', false));
}

function* fetchNestedAccessories(action) {
  yield put(setLoader('nestedAccessories', true));
  if (action.payload.fetchSettings.isRootNode) {
    yield put(setLoader('addAccessory', true));
  }
  const accessoriesAssociations = yield select(getAccessories);
  const response = yield call(fetch, getUrlFiltered(
    `${PRODUCTS_URL}/configurator`,
    {
      'attributes[main_color][]': [action.payload.fetchSettings.color, ''],
      'productGroups[]': action.payload.fetchSettings.accessoryGroup,
      section: action.payload.fetchSettings.section,
    },
  ));
  const furtherAssociations = yield response.json();
  const treeStructureSettings = yield select(getTreeStructureSettings);
  let { level } = treeStructureSettings;
  const { parentId, parentName } = treeStructureSettings;

  if (furtherAssociations['hydra:member'].length > 0) {
    yield put(setTreeStructureSettings(level += 1, parentId, parentName));
    yield put(setAccessoryList({
      ...accessoriesAssociations,
      [level]: furtherAssociations,
    }));

    yield put(setOpenModal('nestedAccessories', true));
  }

  if (action.payload.fetchSettings.isRootNode) {
    yield put(setLoader('addAccessory', false));
  }
  yield put(setLoader('nestedAccessories', false));
}

export function* fetchFiltersSaga({ payload }) {
  yield put(fetchColors(payload));
  yield put(fetchTypes(payload));
  yield put(fetchHeights(payload));
}

export function* fetchColorsSaga({ payload }) {
  yield put(setLoader('color', true));
  const response = yield call(fetch, getUrlFiltered(`${ATTRIBUTES_URL}/main_color`,
    {
      'attributes[product_type]': payload.filters.type,
      'attributes[height]': payload.filters.height,
      section: 'panel',
    }));
  yield call(responseHandler, {
    response,
    storeSaver: setColors,
  });
  yield put(setLoader('color', false));
}

export function* fetchTypesSaga({ payload }) {
  yield put(setLoader('type', true));
  const response = yield call(fetch, getUrlFiltered(`${ATTRIBUTES_URL}/product_type`,
    {
      'attributes[main_color]': payload.filters.color,
      'attributes[height]': payload.filters.height,
      section: 'panel',
    }));

  yield call(responseHandler, {
    response,
    storeSaver: setTypes,
  });
  yield put(setLoader('type', false));
}

export function* fetchHeightsSaga({ payload }) {
  yield put(setLoader('height', true));
  const response = yield call(fetch, getUrlFiltered(`${ATTRIBUTES_URL}/height`,
    {
      'attributes[main_color]': payload.filters.color,
      'attributes[product_type]': payload.filters.type,
      section: 'panel',
    }));

  yield call(responseHandler, {
    response,
    storeSaver: setHeights,
  });
  yield put(setLoader('height', false));
}

export function* fetchProductsBySectionSaga(action) {
  switch (action.payload.fetchSettings.section) {
    default: yield call(fetchPanels, action);
      break;
    case 'post': yield call(fetchPosts, action);
      break;
    case 'corner_post': yield call(fetchCornerPosts, action);
      break;
    case 'accessory':
      if (!(action.payload.fetchSettings.accessoryGroup)) {
        yield call(fetchAccessories, action);
        break;
      } else {
        yield call(fetchNestedAccessories, action);
        break;
      }
    case 'corner_accessory':
      yield call(fetchAccessories, action);
      break;
  }
}

export function* calculateProductsQuantitySaga(action) {
  yield put(setLoader('calculate', true));
  const response = yield call(fetch, getUrlFiltered(CONFIGURE_URL), {
    method: 'POST',
    body: JSON.stringify({
      distance: action.payload.distance,
      panel: action.payload.panel,
      post: action.payload.post,
      color: action.payload.color,
      type: action.payload.type,
      height: action.payload.height,
      cornersQuantity: action.payload.cornersQuantity || 0,
      openingsQuantity: action.payload.openingsQuantity || 0,
      accessories: action.payload.accessories,
      cornerPost: action.payload.cornerPost,
      doublePost: action.payload.doublePost,
    }),
    headers: {
      'Content-Type': 'application/ld+json',
    },
  });

  yield call(responseHandler, {
    response,
    storeSaver: setNecessaryProductsQuantity,
    violations: {
      validationSetter: setValidation,
      validationTarget: 'form',
    },
  });

  yield put(setLoader('calculate', false));
}

export function* watchFetchFiltersSaga() {
  yield takeEvery(String(fetchFilters), fetchFiltersSaga);
}

export function* watchFetchColorsSaga() {
  yield takeEvery(String(fetchColors), fetchColorsSaga);
}

export function* watchFetchTypesSaga() {
  yield takeEvery(String(fetchTypes), fetchTypesSaga);
}

export function* watchFetchHeightsSaga() {
  yield takeEvery(String(fetchHeights), fetchHeightsSaga);
}

export function* watchFetchProductsBySectionSaga() {
  yield takeEvery(String(fetchProductsBySection), fetchProductsBySectionSaga);
}

export function* watchCalculateProductsQuantitySaga() {
  yield takeEvery(String(calculateProductsQuantity), calculateProductsQuantitySaga);
}
